import { useRef } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { useNavigate } from 'react-router-dom';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useAddReachProSupportCaseNote } from 'src/hooks/api/useAddReachProSupportCaseNote';
import { useReachProSupportCaseNotes } from 'src/hooks/api/useReachProSupportCaseNotes';
import { ReachProSupportCaseChatInput } from 'src/navigations/Routes/ReachProSupportCase/ReachProSupportCaseChatInput';
import { ReachProSupportCaseMessage } from 'src/navigations/Routes/ReachProSupportCase/ReachProSupportCaseMessage';
import { ExpandIcon } from 'src/svgs';
import { CrossIcon } from 'src/svgs/Viagogo';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';

import * as styles from './ReachProSupportThreadPopup.css';

export function ReachProSupportThreadPopup({
  workItemId,
  onClose,
}: {
  workItemId: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const nodeRef = useRef<HTMLDivElement>(null);
  const { data: notes, isLoading } = useReachProSupportCaseNotes(workItemId);
  const addNote = useAddReachProSupportCaseNote();

  const handleSend = (content: string) => {
    addNote.mutate({ workItemId, messageContent: content });
  };

  const handleExpand = () => {
    onClose();
    navigate(`/reachpro-support/${workItemId}`);
  };

  return createPortal(
    <Draggable handle={`.${styles.dragHandle}`} bounds="body" nodeRef={nodeRef}>
      <div ref={nodeRef} className={styles.popup}>
        <div className={styles.header}>
          <div className={styles.dragHandle}>Support</div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={handleExpand}
              aria-label="Expand"
            >
              <ExpandIcon size={vars.iconSize.s} withHoverEffect />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={onClose}
              aria-label="Close"
            >
              <CrossIcon size={vars.iconSize.s} withHoverEffect />
            </button>
          </div>
        </div>
        <div className={styles.messages}>
          {isLoading ? (
            <PosSpinner />
          ) : (
            notes?.map((note, index) => (
              <ReachProSupportCaseMessage
                key={note.workItemNoteId?.toString() ?? `note-${index}`}
                message={{
                  id: note.workItemNoteId?.toString() ?? `note-${index}`,
                  content: note.messageContent,
                  senderName: note.senderName,
                  timestamp: stringToUtcDate(note.timestamp),
                  isSent: note.isSent,
                }}
              />
            ))
          )}
        </div>
        <ReachProSupportCaseChatInput
          onSend={handleSend}
          isLoading={addNote.isPending}
        />
      </div>
    </Draggable>,
    document.body
  );
}
